import { msg } from "@omegagrid/localize";
import { MenuItem, IconSpec, dates } from "@omegagrid/core";
import { NodeModel, NodeSource, TreeOptions, TreeSource } from "@omegagrid/tree";
import { BrowserAdapter } from "@reporting/rp-app-host";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { ReportContext } from "@reporting/rp-types/analytics";
import { ModuleDefinition } from "@reporting/rp-types/definition";
import { getVisualizationMap, Sidebar } from "../components";
import { add, deleteContext } from "../actions";
import { css, html, render } from "lit";
import { EntityAccessType } from "@reporting/rp-types/users";
import rp from "@reporting/rp-app-host";
import { orderFoldersFirst } from "./contextTree";

const formatDate = (d: string) => dates.format(new Date(d), dates.getDateFormat() + ' HH:mm');

const createModuleHeader = (module: ModuleDefinition, shared = false): NodeSource => ({
	k: module.module,
	v: `${module.name}${shared ? ` - ${msg('Shared')}` : ''}`,
	t: `module-${module.module}-${shared ? 'shared' : ''}`,
	l: 0,
	count: 1,
	collapsed: true,
	folder: {
		openIcon: {
			icon: 'folder-open',
			statusIcon: ['fas', 'cube'],
			statusColor: module.color
		},
		closedIcon: {
			icon: 'folder',
			statusIcon: ['fas', 'cube'],
			statusColor: module.color
		},
	}
});

export const getContextIcon = (context: ReportContext, module?: ModuleDefinition): IconSpec => {
	const view = context.context_view ? context.context_view.replace(/\d+$/, '') : '';
	const visualizationMap = getVisualizationMap();
	const visualization = visualizationMap.get(view) ?? visualizationMap.get(context.module);
	return {
		icon: view.startsWith('custom-') ? 'puzzle-piece' : (visualization?.icon || 'cube'),
		color: module?.color
	}
}

const createContextItems = (context: ReportContext, parents: string[], module: ModuleDefinition): {
	items: NodeSource[], parents: string[]
} => {
	const items: NodeSource[] = [];
	const chunks = context.name.split('/');
	const name = chunks.pop();
	let common = true;
	for (let i = 0; i < chunks.length; i++) {
		const dir = chunks[i];
		common = common && parents.length > i && parents[i] === dir;
		if (common) continue;
		items.push({
			k: dir,
			v: dir,
			l: i + 1,
			collapsed: true,
			folder: BrowserAdapter.folderOptions
		});
	}

	items.push({
		k: context.id_context.toString(),
		v: name,
		l: chunks.length + 1,
		icon: getContextIcon(context, module),
		props: {context}
	});

	return {items: items, parents: chunks};
};


export class ContextBrowserAdapter extends BrowserAdapter {

	constructor(
		private modules: Map<ModuleCode, ModuleDefinition>,
	) { super() }

	async getTree(): Promise<TreeSource> {
		const items = await rp.ms('analytics').get<ReportContext[]>(`namedContext`, {
			fields: ['idz', 'id_context', 'name', 'module', 'shared', 'inherited', 'context_view', 'user_name', 'login_name', 'date_created'],
			order: [['module', 'asc'], ['shared', 'asc'], ['name', 'asc']]
		});

		const itemGroups = new Map<ModuleCode, {contexts: ReportContext[], sharedContexts: ReportContext[]}>();
		items.forEach(item => {
			const moduleCode = item.module as ModuleCode
			if (!itemGroups.has(moduleCode)) {
				itemGroups.set(moduleCode, {contexts: [], sharedContexts: []});
			}
			if (item.shared) itemGroups.get(moduleCode).sharedContexts.push(item);
			else itemGroups.get(moduleCode).contexts.push(item);
		});

		const treeItems: NodeSource[] = [];
		this.modules.forEach((module, m) => {
			const itemGroup = itemGroups.get(m);
			if (!itemGroup) return;

			let parents: string[] = [];

			if (itemGroup.contexts.length) {
				treeItems.push(createModuleHeader(module));
				orderFoldersFirst(itemGroup.contexts, c => c.name).forEach((item) => {
					const result = createContextItems(item, parents, module);
					result.items.forEach((item) => treeItems.push(item));
					parents = result.parents;
				});
			}

			if (itemGroup.sharedContexts.length) {
				treeItems.push(createModuleHeader(module, true));
				parents = [];
				orderFoldersFirst(itemGroup.sharedContexts, c => c.name).forEach((item) => {
					const result = createContextItems(item, parents, module);
					result.items.forEach((item) => treeItems.push(item));
					parents = result.parents;
				});
			}
		});

		return treeItems;
	}

	async getMenuItems(node: NodeModel): Promise<MenuItem[]> {
		if (!node.properties?.context) return [];
		return [
			{key: 'open', value: msg('Open'), icon: 'book-open'},
			{key: 'openDialog', value: msg('Open in dialog'), icon: 'window', divider: true},
			{key: 'properties', value: msg('Properties'), icon: 'pen-to-square'},
			{key: 'filters', value: msg('Filters'), icon: 'filter'},
			{key: 'contextData', value: msg('Context json'), icon: 'brackets-curly'},
			{key: 'shareUsers', value: `${msg('Sharing')} - ${msg('Users')}` , icon: 'users'},
			// {key: 'shareAccountings', value: `${msg('Sharing')} - ${msg('Companies')}` , icon: 'buildings', divider: true},
			{key: 'addToDashboard', value: `${msg('Add to dashboard')}` , icon: 'plus'},
			{key: 'addToSpreadsheet', value: `${msg('Add to spreadsheet')}` , icon: 'plus', divider: true},
			// {key: 'addToHomepage', value: `${msg('Add to homepage')}` , icon: 'plus', divider: true},
			{key: 'delete', value: msg('Delete'), icon: 'trash', color: '#ff0000'}
		];
	}

	async onAction(key: string, node: NodeModel) {
		const context = node.properties.context as ReportContext;
		switch (key) {
			case 'filters':
				const sidebar = await rp.host.components.get('analytics') as Sidebar;
				sidebar.updateState({activeTab: 'filters', components: {filters: {
					idContext: context.id_context,
					module: context.module as ModuleCode
				}}}, true);
				break;
			case 'open':
				rp.host.open(`analytics-reportContainer-${context.id_context}`, {title: context.name});
				break;
			case 'openDialog':
				rp.host.openDialog({
					id: `analytics-reportContainer-${context.id_context}`,
					caption: context.name,
					closable: true,
					width: Math.min(800, rp.host.offsetWidth * 0.8),
					height: Math.min(600, rp.host.offsetHeight * 0.8),
					resizable: true,
					location: 'center',
				});
				break;
			case 'delete':
				if (context) {
					await deleteContext(context);
					this.browser.deleteNode(node);
				}
				break;
			case 'properties':
				rp.host.openModalSidebar({
					id: `analytics-contextForm-${context.id_context}`,
					caption: `#${context.id_context} ${context.name}`
				});
				break;
			case 'shareUsers':
				rp.host.open(`users-entityUsers-${EntityAccessType.Context}-${context.id_context}`, { title: `${msg('Share')}: ${context.name}` });
				break;
			case 'contextData':
				rp.host.open(`analytics-contextDataEditor-${context.id_context}`);
				break;
			case 'addToDashboard':
				add(context, 'D');
				break;
			case 'addToSpreadsheet':
				add(context, 'S');
				break;
		}
	}

	getTooltip(node: NodeModel) {
		const properties = node.properties;
		if (!properties) return null;

		const context = properties.context as ReportContext;

		return html`
			<table style="border-collapse: collapse;">
				<tr>
					<td>${msg('id')}:</td>
					<td>${context.id_context}</td>
				</tr>
				<tr>
					<td>${msg('User')}:</td>
					<td>${context.login_name || context.user_name}</td>
				</tr>
				<tr>
					<td>${msg('Created')}:</td>
					<td>${formatDate(context.date_created)}</td>
				</tr>
			</table>
		`;
	}

	getOptions(): Partial<TreeOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			contextMenuButton: true,
			customStyle: css`
				.txt {
					container-type: inline-size;
				}

				.item-text {
					flex: 1;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

				.item-id {
					display: none;
					font-size: 8px;
				}

				@container (width > 400px) {
					.item-id {
						display: inline;
					}
				}
			`,
			nodeRenderer: (div, node) => {
				const context = node.properties?.context as ReportContext;
				if (!context) {
					div.textContent = node.value;
					return;
				}

				div.style.display = 'flex';
				div.style.flexDirection = 'row';

				render(html`
					<div class="item-text">${node.value}</div>
					<div class="item-id">#${context.id_context}</div>
				`, div);
			},
		});
	}
}
